import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Bot, PenLine, MessageCircle, Send, Loader2, CheckCircle, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { Header, Footer } from "../components/Layout";
import { AuthGate, getAdminToken } from "../components/AuthGate";
import seoData from "../data/seoVilles.json";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Redacteur() {
  const [topic, setTopic] = useState("");
  const [ville, setVille] = useState("");
  const [articles, setArticles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    const res = await axios.get(`${API}/ai/articles`);
    setArticles(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      await axios.post(`${API}/ai/generate-article`, { topic, ville }, { timeout: 180000 });
      setTopic("");
      load();
    } catch (e) {
      setError("La génération a échoué, réessayez dans un instant.");
    } finally {
      setGenerating(false);
    }
  };

  const publish = async (id) => { await axios.post(`${API}/ai/articles/${id}/publish`); load(); };
  const remove = async (id) => { await axios.delete(`${API}/ai/articles/${id}`); load(); };

  return (
    <div>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
        <h3 className="font-bold text-lg text-[#0f172a] mb-4">Générer un nouvel article</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Sujet (optionnel, ex: frelons asiatiques en été)"
            data-testid="ai-topic-input"
            className="px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 outline-none text-sm"
          />
          <select
            value={ville}
            onChange={(e) => setVille(e.target.value)}
            data-testid="ai-ville-select"
            className="px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 outline-none text-sm bg-white"
          >
            <option value="">Zone : Nice et Alpes-Maritimes (par défaut)</option>
            {seoData.villes.filter((v) => v.dept === "06").map((v) => (
              <option key={v.slug} value={`${v.nom} (${v.dept})`}>{v.nom}</option>
            ))}
            <option value="Paris et Île-de-France">Paris et Île-de-France</option>
          </select>
        </div>
        <button
          onClick={generate}
          disabled={generating}
          data-testid="ai-generate-btn"
          className="inline-flex items-center space-x-2 bg-emerald-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-600 transition-colors disabled:opacity-60"
        >
          {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : <PenLine className="w-5 h-5" />}
          <span>{generating ? "Rédaction en cours (30-60s)..." : "Rédiger l'article"}</span>
        </button>
        {error && <p className="text-red-500 text-sm mt-3" data-testid="ai-generate-error">{error}</p>}
        <p className="text-gray-400 text-xs mt-3">
          Laissez le sujet vide : l'IA choisit un sujet de saison. Vous validez avant publication.
        </p>
      </div>

      <h3 className="font-bold text-lg text-[#0f172a] mb-4">Articles générés ({articles.length})</h3>
      {articles.length === 0 && (
        <p className="text-gray-500" data-testid="ai-articles-empty">Aucun article généré pour l'instant.</p>
      )}
      <div className="space-y-4">
        {articles.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`ai-article-${a.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex-1 min-w-[250px]">
                <div className="flex items-center space-x-2 mb-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${a.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {a.status === "published" ? "Publié" : "Brouillon"}
                  </span>
                  <span className="text-xs text-gray-400">{a.category} • {a.date}</span>
                </div>
                <p className="font-bold text-[#0f172a]">{a.title}</p>
                <p className="text-gray-500 text-sm mt-1">{a.excerpt}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button onClick={() => setExpanded(expanded === a.id ? null : a.id)} data-testid={`ai-preview-${a.id}`}
                  className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200" title="Aperçu">
                  {expanded === a.id ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {a.status === "draft" ? (
                  <button onClick={() => publish(a.id)} data-testid={`ai-publish-${a.id}`}
                    className="inline-flex items-center space-x-1 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-semibold hover:bg-emerald-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Publier</span>
                  </button>
                ) : (
                  <Link to={`/blog/${a.id}`} className="inline-flex items-center space-x-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200">
                    <ExternalLink className="w-4 h-4" />
                    <span>Voir</span>
                  </Link>
                )}
                <button onClick={() => remove(a.id)} data-testid={`ai-delete-${a.id}`}
                  className="p-2 rounded-lg text-gray-300 hover:text-red-500" title="Supprimer">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {expanded === a.id && (
              <div className="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-700 whitespace-pre-wrap max-h-96 overflow-y-auto bg-gray-50 rounded-xl p-4">
                {a.content}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Conseiller() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const sessionId = useRef(
    localStorage.getItem("seo_chat_session") ||
      (() => { const id = `s-${Date.now()}`; localStorage.setItem("seo_chat_session", id); return id; })()
  );

  useEffect(() => {
    axios.get(`${API}/ai/chat/${sessionId.current}`).then((r) => setMessages(r.data.map((m) => ({ role: m.role, content: m.content }))));
  }, []);

  useEffect(() => {
    // Défilement interne + remise en vue minimale de la dernière bulle (sans faire sauter la page)
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    bottomRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getAdminToken()}` },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line || line === "[DONE]") continue;
          try {
            const payload = JSON.parse(line);
            if (payload.d) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.d };
                return copy;
              });
            }
          } catch (err) { /* chunk partiel */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Erreur de connexion, réessayez." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const suggestions = [
    "Comment remonter sur « dératisation Cannes » ?",
    "Analyse mes positions et donne-moi 3 actions prioritaires",
    "Quels sujets d'articles publier ce mois-ci ?",
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 flex flex-col h-[70vh] md:h-[600px]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 flex flex-col" data-testid="ai-chat-messages">
        {messages.length === 0 && (
          <div className="text-center mt-10">
            <Bot className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">Posez-moi vos questions SEO : je connais vos positions, vos clics et votre site.</p>
            <div className="flex flex-col items-center space-y-2">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => setInput(s)} className="text-sm text-emerald-600 bg-emerald-50 px-4 py-2 rounded-full hover:bg-emerald-100">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Messages ancrés en bas du conteneur (mt-auto) : toujours visibles près de la zone de saisie */}
        <div className="mt-auto space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#0f172a] text-white" : "bg-gray-100 text-gray-800"}`}>
                {m.content ||
                  (streaming && i === messages.length - 1 ? (
                    <span className="inline-flex items-center space-x-2 text-gray-500" data-testid="ai-chat-thinking">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>L'assistant réfléchit...</span>
                    </span>
                  ) : "")}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>
      <div className="border-t border-gray-100 p-4 flex space-x-3">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Votre question SEO..."
          disabled={streaming}
          data-testid="ai-chat-input"
          className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 outline-none text-sm"
        />
        <button onClick={send} disabled={streaming} data-testid="ai-chat-send"
          className="px-5 py-3 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 disabled:opacity-60">
          {streaming ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}

export default function AgentSEO() {
  return (
    <AuthGate>
      <AgentSEOInner />
    </AuthGate>
  );
}

function AgentSEOInner() {
  const [tab, setTab] = useState("redacteur");

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <Helmet>
        <title>Agent IA SEO – Espace privé | Azur Tech</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header />

      <section className="pt-28 md:pt-36 pb-10 bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] text-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-3 mb-3">
            <Bot className="w-8 h-8 text-emerald-400" />
            <h1 className="text-3xl md:text-4xl font-bold">Agent IA Référencement</h1>
          </div>
          <p className="text-gray-300 max-w-2xl">
            Votre assistant personnel : il rédige des articles de blog optimisés et vous conseille sur votre stratégie SEO.
          </p>
          <Link to="/suivi-seo" className="inline-block mt-4 text-emerald-400 hover:underline text-sm">
            ← Retour au tableau de bord SEO
          </Link>
        </div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex space-x-2 mb-8">
            <button onClick={() => setTab("redacteur")} data-testid="tab-redacteur"
              className={`inline-flex items-center space-x-2 px-5 py-3 rounded-xl font-semibold text-sm transition-colors ${tab === "redacteur" ? "bg-[#0f172a] text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100"}`}>
              <PenLine className="w-4 h-4" />
              <span>Rédacteur d'articles</span>
            </button>
            <button onClick={() => setTab("conseiller")} data-testid="tab-conseiller"
              className={`inline-flex items-center space-x-2 px-5 py-3 rounded-xl font-semibold text-sm transition-colors ${tab === "conseiller" ? "bg-[#0f172a] text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100"}`}>
              <MessageCircle className="w-4 h-4" />
              <span>Conseiller SEO</span>
            </button>
          </div>

          {tab === "redacteur" ? <Redacteur /> : <Conseiller />}
        </div>
      </section>

      <Footer />
    </div>
  );
}
